import { defineStore } from "pinia";
import api from "@/modules/api";
import { Mate, StoryBook } from "@/types/entity";
import { useMate } from "@/stores/mate";
import { useStory } from "@/stores/story";
import { useReply } from "@/stores/reply";
import { StorageGet } from "@/tools/localStorage";
import { useAdmire } from "./admire";
import { showConfirmDialog } from "vant";
import i18n from "@/locales";

const PAGE_SIZE = 999;

const defaultDiscover = () => ({
  isFinished: false,
  page: 1,
  scrollTop: 0,
  data: [] as any[],
});
export const useStoryBook = defineStore("storybook", {
  state: () => {
    return {
      storyBook: {
        allList: [] as string[],
      },
      storyScrollMap: {} as Record<string, number>,
      storyType: 1, // 0: 草稿, 1: 已发布
      storyBookMap: {} as Record<string, StoryBook>,

      // 编辑
      edit: {
        tabType: 0 as 0 | 1 | 2,
        showSelectType: false,

        generateTarget: "" as StoryGenerateParamsKey, // 目标自动生成
        showGenerate: false, // 是否显示自动生成页面
        generateChecked: [] as Array<StoryGenerateParamsKey | string>, // 选中的自动生成的内容
        generateContentNodeIdx: -1, // 选中的自动生成的节点
        generateContentNodeTarget: "" as keyof StoryContentNode, // 选中的自动生成的节点

        showSelectChapter: false, // 是否显示选择章节弹窗
        selectChapterId: 0, // 选择的章节id
        selectChapterType: 0 as 0 | 1, // 选择章节类型 0: 文风类型, 1: 前置故事
        chapterLoading: false, // 是否正在加载章节列表

        showGenerateLoadingModal: false,
        styleChapterId: 0,
        isGenerating: false,
        generateContent: "",

        isShowStoryBook: false,
      },
      role: {
        tabType: 0 as 0 | 1 | 2, // 0: 我的OC, 1: 我守护的, 2: 被守护
      },
      form: {
        type: 1, // 类型
        characterzz: [] as StoryCharacter[], // 故事人物
        description: "", // 故事简述
        momentIdzz: [] as number[], // 关联动态

        exampleText: "", // 文风参考

        previousStoryId: 0, // 前置章节
        beginning: "", // 开头
        bodyNodezz: [] as StoryContentNode[], // 故事节点
        ending: "", // 结尾
      } as StoryGenerateParams,

      isInit: false,

      // 发现页面

      isFetching: false,
      isFinished: false,
      currentTab: "hot" as "hot" | "new" | "my",
      discoverList: {
        hot: defaultDiscover(),
        new: defaultDiscover(),
        my: defaultDiscover(),
      },
    };
  },
  getters: {
    allStoryBookList(state): StoryBook[] {
      return state.storyBook.allList
        .map((id) => state.storyBookMap[id])
        .sort((a, b) => (b.dtPublish ? -1 : 1));
    },
    currentStoryMateList(state) {
      const { characterzz } = state.form;
      const mateMap: Record<number, Mate> = {};
      useMate().list.forEach(
        (v) => (mateMap[v.id] = JSON.parse(JSON.stringify(v)))
      );
      useAdmire().admiring.list.forEach(
        (v) => (mateMap[v.mateId] = JSON.parse(JSON.stringify(v.mate)))
      );
      useAdmire().admirer.list.forEach(
        (v) => (mateMap[v.fanId] = JSON.parse(JSON.stringify(v.fan)))
      );
      return characterzz!.map((v) => {
        const mate = mateMap[v.mateId];
        mate.role = v.role;
        return mate;
      });
    },
    canSelectStoreMateList(state) {
      const { characterzz } = state.form;
      const alreadySelectMateMap: Record<number, boolean> = {};
      characterzz!.forEach((v) => (alreadySelectMateMap[v.mateId] = true));
      const mateMap: Mate[] = [];
      if (state.role.tabType === 0) {
        useMate().list.forEach(
          (v) =>
            v.status === 1 &&
            !alreadySelectMateMap[v.id] &&
            mateMap.push({ ...v, role: 1 })
        );
      }
      if (state.role.tabType === 1) {
        useAdmire().admiring.list.forEach(
          ({ mate: v }) =>
            v.status === 1 &&
            !alreadySelectMateMap[v.id] &&
            mateMap.push({ ...v, role: 1 })
        );
      }
      if (state.role.tabType === 2) {
        useAdmire().admirer.list.forEach(
          ({ fan: v }) =>
            v.status === 1 &&
            !alreadySelectMateMap[v.id] &&
            mateMap.push({ ...v, role: 1 })
        );
      }
      return mateMap;
    },
    currentList(state) {
      return state.discoverList[state.currentTab];
    },
  },
  actions: {
    updateStoryBookMap(storyBookList: StoryBook[]) {
      storyBookList.forEach((item) => {
        this.storyBookMap[item.uuid] = item;
      });
      const reply = useReply();
      reply.updateMap(
        storyBookList.map((item) => item.reply).filter((item) => item)
      );
      storyBookList.forEach((item) => {
        item.reply = reply.getReply(item.replyId)!;
      });
    },
    changeStoryBookType(type: 0 | 1) {
      this.storyType = type;
    },
    async getStoryBookList(force = false): Promise<StoryBook[]> {
      if (
        !force &&
        this.storyBook.allList &&
        this.storyBook.allList.length > 0 &&
        this.isInit
      ) {
        return this.allStoryBookList;
      } else {
        const mate = useMate();
        const storyList = await this.fetchStoryBookList({
          mateId: mate.userMate.id,
        });
        this.isInit = true;
        this.storyBook.allList = storyList.map((item) => item.uuid);
        return storyList;
      }
    },
    async fetchStoryBookList(
      filter: { mateId?: number; published?: 0 | 1 },
      pagination = { page: 1, perPage: PAGE_SIZE },
      sort = { field: "dtUpdate", order: "desc" as "desc" | "asc" }
    ): Promise<StoryBook[]> {
      const res = await api.readStoryBookPage({
        filter,
        pagination,
        sort,
      });
      this.updateStoryBookMap(res.page.data);
      return res.page.data;
    },
    async fetchStoryBookListByMateId(
      mateId: number,
      pagination = { page: 1, perPage: PAGE_SIZE },
      sort = { field: "dtPublish", order: "desc" as "desc" | "asc" }
    ): Promise<StoryBook[]> {
      const mate = useMate();
      return await this.fetchStoryBookList(
        { mateId, published: mateId === mate.userMate.id ? undefined : 1 },
        pagination,
        sort
      );
    },
    disableCache(clearData = false): void {
      if (clearData) {
        this.storyBook.allList = [];
      }
    },
    async createStoryBook(
      params: Parameters<typeof api.createOneStoryBook>[0]
    ) {
      const res = await api.createOneStoryBook(params);
      const newStoryBook = await this.getOrFetchStoryItem(res.item.uuid);
      this.updateStoryBookMap([newStoryBook]);
      this.storyBook.allList.unshift(res.item.uuid);
      return res.item;
    },
    async getOrFetchStoryItem(uuid: string, force = false): Promise<StoryBook> {
      const storyBook = this.storyBookMap[uuid];
      if (!force && storyBook) {
        return storyBook;
      } else {
        return await this.fetchStoryItem(uuid);
      }
    },
    async fetchStoryItem(uuid: string): Promise<StoryBook> {
      const { item } = await api.readOneStoryBook(uuid);
      this.updateStoryBookMap([item]);
      return this.storyBookMap[item.uuid];
    },
    async updateStoryBook(
      id: number,
      params: Parameters<typeof api.updateOneStoryBook>[1]
    ) {
      const res = await api.updateOneStoryBook(id, params);
      await this.getOrFetchStoryItem(res.item.uuid, true);
      return this.storyBookMap[res.item.uuid];
    },
    async deleteStoryBook(storyBook: StoryBook) {
      await api.deleteOneStoryBook(storyBook.id);
      this.storyBook.allList = this.storyBook.allList.filter(
        (uuid) => uuid !== storyBook.uuid
      );
      const storyStore = useStory();
      if (storyStore.storyBookId2StoryUuids[storyBook.id]) {
        storyStore.story.list = storyStore.story.list.filter(
          (uuid) =>
            !storyStore.storyBookId2StoryUuids[storyBook.id].includes(uuid)
        );
        storyStore.storyBookId2StoryUuids[storyBook.id] = [];
      }
    },
    initForm(id?: number) {
      const key = id ? `story-${id}` : "story";
      const form = StorageGet(key);
      if (!form) return this.resetForm();
      this.form = form;
    },
    resetForm() {
      this.edit = {
        tabType: 0,
        showSelectType: false,

        generateTarget: "" as StoryGenerateParamsKey, // 目标自动生成
        showGenerate: false, // 是否显示自动生成页面
        generateChecked: [] as Array<StoryGenerateParamsKey | string>, // 选中的自动生成的内容
        generateContentNodeIdx: -1, // 选中的自动生成的节点
        generateContentNodeTarget: "" as keyof StoryContentNode, // 选中的自动生成的节点

        showSelectChapter: false, // 是否显示选择章节弹窗
        selectChapterId: 0, // 选择的章节id
        selectChapterType: 0 as 0 | 1, // 选择章节类型 0: 文风类型, 1: 前置故事
        chapterLoading: false, // 是否正在加载章节列表

        isShowStoryBook: false,

        showGenerateLoadingModal: false,
        isGenerating: false,
        generateContent: "",

        styleChapterId: 0,
      };
      this.form = {
        type: 1, // 类型
        characterzz: [], // 故事人物
        description: "", // 故事简述
        momentIdzz: [], // 关联动态

        exampleText: "", // 文风参考

        previousStoryId: 0, // 前置章节
        beginning: "", // 开头
        bodyNodezz: [], // 故事节点
        ending: "", // 结尾
      };
    },
    resetOneForm(
      key: StoryGenerateParamsKey,
      nodeIdx?: number,
      nodeTarget?: keyof StoryContentNode
    ) {
      showConfirmDialog({
        title: i18n.global.t("ConfirmModal.promptTitle"),
        message: i18n.global.t("Message.whetherResetThisContent"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      }).then(() => {
        if (key === "bodyNodezz" && nodeIdx !== undefined && nodeTarget) {
          this.form.bodyNodezz![nodeIdx][nodeTarget] = "" as any;
        } else {
          this.form[key] = "" as any;
        }
      });
    },
    openGenerateModal(
      target: StoryGenerateParamsKey,
      nodeIdx?: number,
      nodeTarget?: keyof StoryContentNode
    ) {
      this.edit.generateTarget = target;
      if (target === "bodyNodezz") {
        this.edit.generateContentNodeIdx = nodeIdx!;
        this.edit.generateContentNodeTarget = nodeTarget!;
      } else {
        this.edit.generateContentNodeIdx = -1;
        this.edit.generateContentNodeTarget = "" as keyof StoryContentNode;
      }
      this.edit.showGenerate = true;
    },

    async fetchPopularStoryList(): Promise<void> {
      if (this.isFetching) return;
      const list = this.currentList;
      const { isFinished, page } = list;
      if (isFinished) return;

      this.isFetching = true;
      const resp = await api.readStoryBookPoplarPage({
        tab: this.currentTab,
        pagination: { perPage: 10, page },
      });
      list.page++;
      const respData = resp.page.data;
      if (respData.length < 10) list.isFinished = true;
      const tmpList = respData
        .map((v: any) => {
          const { media, uuid, mate } = v;
          return {
            ...v,
            src: media?.url,
            ratio: "1/1",
            url: `/story-book/${uuid}`,
            author: mate,
          };
        })
        .sort(() => Math.random() - 0.5);

      list.data = [...list.data, ...tmpList];
      this.isFetching = false;
    },
  },
});
